import { DeleteOutlined, HolderOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Input, InputNumber, Modal, Select, Tooltip, message } from 'antd';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { create_race, get_race, get_race_sics } from '../services/api.service';
import { formatdateYYYYMMDD, MLCompetitor, MLRace } from '../types';
import './newrace.scss';

const STATUS_OPTIONS = [{ value: 'DP', label: 'DP' }, { value: 'NP', label: 'NP' }];
const RACE_TYPES = ['PLAT', 'TROT', 'STEEPLE', 'CROSS', 'MONTÉ', 'ATTELÉ', 'HAIES'];

/* -------------------------------------------------------------------------------- *\
|                               NewRacePage
\* -------------------------------------------------------------------------------- */
export const NewRacePage: FC = () =>
{
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [sicOptions, setSicOptions] = useState<{ label: ReactNode; options: { value: string; label: string }[] }[]>([]);
    const [sicMap, setSicMap] = useState<Record<string, string>>({});
    const [codesMap, setCodesMap] = useState<Record<string, string[]>>({});

    useEffect(() =>
    {
        get_race_sics().then(sics =>
        {
            const map: Record<string, string> = {};
            const cmap: Record<string, string[]> = {};
            const grouped: Record<string, { sic: string; name: string }[]> = {};
            for (const s of sics)
            {
                map[s.sic] = s.name;
                if (s.codes?.length) cmap[s.sic] = s.codes;
                const src = s.source || 'Other';
                if (!grouped[src]) grouped[src] = [];
                grouped[src].push({ sic: s.sic, name: s.name });
            }
            setSicMap(map);
            setCodesMap(cmap);
            setSicOptions(Object.entries(grouped).map(([src, items]) => ({
                label: <span style={{ color: '#888', fontSize: 11, textTransform: 'uppercase' }}>{src}</span>,
                options: items.map(i => ({ value: i.sic, label: `${i.sic} — ${i.name}` })),
            })));
        }).catch(() => {});
    }, []);

    const [dateVal, setDateVal] = useState('');
    const [timeVal, setTimeVal] = useState('');
    const [meetingdateVal, setMeetingdateVal] = useState(''); // "YYYY-MM-DD", vide = date de la course (toujours minuit UTC)
    const [number, setNumber] = useState<number>(1);
    const [sic, setSic] = useState('');
    const [trackname, setTrackname] = useState('');
    const [name, setName] = useState('');
    const [distance, setDistance] = useState<number>(2000);
    const [type, setType] = useState('PLAT');
    const [trackcode, setTrackcode] = useState('');
    const [source, setSource] = useState('USER');
    const [comment, setComment] = useState('');
    const [competitors, setCompetitors] = useState<MLCompetitor[]>([]);

    /* -------------------------------------------------------------------------------- *\
    |                               competitor helpers
    \* -------------------------------------------------------------------------------- */
    function addCompetitor()
    {
        const next = competitors.length > 0 ? Math.max(...competitors.map(c => c.number)) + 1 : 1;
        setCompetitors(prev => [...prev, Object.assign(new MLCompetitor(), { number: next, status: 'DP', distance })]);
    }

    function updateCompetitor(idx: number, patch: Partial<MLCompetitor>)
    {
        setCompetitors(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
    }

    function removeCompetitor(idx: number)
    {
        setCompetitors(prev => prev.filter((_, i) => i !== idx));
    }

    const jsonInputRef = useRef<HTMLInputElement>(null);
    const dragIdx = useRef<number | null>(null);
    const [dragOver, setDragOver] = useState<number | null>(null);

    function handleDrop(toIdx: number)
    {
        const fromIdx = dragIdx.current;
        setDragOver(null);
        if (fromIdx === null || fromIdx === toIdx) return;
        setCompetitors(prev =>
        {
            const numbers = prev.map(c => c.number);
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next.map((c, i) => ({ ...c, number: numbers[i] }));
        });
        dragIdx.current = null;
    }

    /* -------------------------------------------------------------------------------- *\
    |                               handleCancel
    \* -------------------------------------------------------------------------------- */
    function handleCancel()
    {
        Modal.confirm({
            title: <div style={{ color: 'white', borderBottom: '1px solid #fff', paddingBottom: 10 }}>Reset all fields</div>,
            icon: null,
            content: <div style={{ color: 'white', marginTop: 8 }}>All current data will be lost.</div>,
            okText: 'Reset',
            cancelText: 'Cancel',
            onOk: () =>
            {
                setDateVal('');
                setTimeVal('');
                setMeetingdateVal('');
                setNumber(1);
                setSic('');
                setTrackname('');
                setName('');
                setDistance(2000);
                setType('PLAT');
                setTrackcode('');
                setSource('USER');
                setComment('');
                setCompetitors([]);
            },
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               handleImportJson
    \* -------------------------------------------------------------------------------- */
    function handleImportJson(file: File)
    {
        const reader = new FileReader();
        reader.onload = (e) =>
        {
            try
            {
                const race: MLRace = JSON.parse(e.target?.result as string);
                setDateVal('');
                setTimeVal('');
                setMeetingdateVal('');
                setNumber(1);
                setSic('');
                setTrackname('');
                setName('');
                setDistance(2000);
                setType('PLAT');
                setTrackcode('');
                setSource('USER');
                setComment('');
                setCompetitors([]);
                const meetingDate = new Date(race.meetingdate || race.date);
                setDateVal(meetingDate.toISOString().substring(0, 10));
                if (race.meetingdate)
                {
                    const md = new Date(race.meetingdate);
                    if (!isNaN(md.getTime())) setMeetingdateVal(md.toISOString().substring(0, 10));
                }
                if (race.date)
                {
                    const d = new Date(race.date);
                    setTimeVal(d.toISOString().substring(11, 16));
                }
                if (race.number)     setNumber(race.number);
                if (race.sic)        { setSic(race.sic); setTrackname(sicMap[race.sic] || race.trackname || ''); }
                if (race.name)       setName(race.name);
                if (race.distance)   setDistance(race.distance);
                if (race.type)       setType(race.type);
                if (race.trackcode)  setTrackcode(race.trackcode);
                if (race.source)     setSource(race.source);
                if (race.comment)    setComment(race.comment);
                if (race.competitors) setCompetitors(race.competitors);
                message.success('Race imported');
            }
            catch { message.error('Invalid JSON file'); }
        };
        reader.readAsText(file);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               exportjson
    \* -------------------------------------------------------------------------------- */
    function exportjson()
    {
        const race = Object.assign(new MLRace(), {
            meetingdate: (meetingdateVal || dateVal) ? `${meetingdateVal || dateVal}T00:00:00.000Z` : '',
            date: dateVal ? `${dateVal}T${timeVal || '00:00'}:00.000Z` : '',
            number, sic: sic.trim().toUpperCase(), trackname, name, distance, type, trackcode, source, comment, competitors,
        });
        const blob = new Blob([JSON.stringify(race, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${dateVal.replace(/-/g, '')}${sic}${number}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               exportracing
    \* -------------------------------------------------------------------------------- */
    function exportracing()
    {
        const meetingdate = new Date(`${dateVal}T00:00:00.000Z`);
        const dateStr = `${String(meetingdate.getUTCDate()).padStart(2, '0')}/${String(meetingdate.getUTCMonth() + 1).padStart(2, '0')}/${meetingdate.getUTCFullYear()}`;

        const racing = {
            Date: dateStr,
            IDHippo: sic.trim().toUpperCase(),
            LibHippo: trackname,
            numR: 1,
            courses: [{
                NumC: number,
                HeureC: timeVal || '00:00',
                LibC: name,
                codeParcours: trackcode,
                NDPart: competitors.length,
                Dist: distance,
                Disc: type,
                partants: competitors.map(c => ({
                    Num: c.number,
                    EtatP: c.status || '',
                    DistP: c.distance || 0,
                    NomC: c.name || '',
                    Jock: c.driver || '',
                    Entr: c.trainer || '',
                    Prop: c.owner || ''
                }))
            }]
        };

        const blob = new Blob([JSON.stringify(racing, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${dateVal.replace(/-/g, '')}${sic.trim().toUpperCase()}_racing.json`;
        a.click();
        URL.revokeObjectURL(url);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               handleSave
    \* -------------------------------------------------------------------------------- */
    async function handleSave()
    {
        if (!dateVal) { message.error('Date is required'); return; }
        if (!sic.trim()) { message.error('SIC is required'); return; }
        if (!number) { message.error('Race number is required'); return; }

        const race = Object.assign(new MLRace(), {
            meetingdate: `${meetingdateVal || dateVal}T00:00:00.000Z`,
            date: timeVal ? `${dateVal}T${timeVal}:00.000Z` : `${dateVal}T00:00:00.000Z`,
            number,
            sic: sic.trim().toUpperCase(),
            trackname: trackname.trim(),
            name: name.trim(),
            distance,
            type,
            trackcode: trackcode.trim(),
            source: source.trim() || 'USER',
            comment: comment.trim(),
            competitors,
        });

        const doCreate = async (override: boolean = false) =>
        {
            setSaving(true);
            try
            {
                const r = await create_race(race, override);
                if (r.status)
                {
                    message.success('Race created');
                    navigate(`/race/${formatdateYYYYMMDD(new Date(race.meetingdate))}/${race.sic}/${race.number}`);
                }
                else
                {
                    message.error(r.message || 'Error');
                }
            }
            catch { message.error('Save error'); }
            finally { setSaving(false); }
        };

        console.log (dateVal, sic.trim().toUpperCase(), number)
        const existing = await get_race(formatdateYYYYMMDD(new Date(race.meetingdate)), sic.trim().toUpperCase(), number).catch(() => null);
        if (existing)
        {
            Modal.confirm({
                title: <div style={{ color: 'white', borderBottom: '1px solid #fff', paddingBottom: 10 }}>Race already exists</div>,
                icon: null,
                content: <div style={{ color: 'white', marginTop: 8 }}>Race <b>{sic.trim().toUpperCase()} #{number}</b> on {dateVal} already exists. Overwrite it?</div>,
                okText: 'Overwrite',
                okButtonProps: { danger: true },
                cancelText: 'Cancel',
                onOk: () => doCreate(true),
            });
        }
        else
        {
            doCreate();
        }
    }

    /* -------------------------------------------------------------------------------- *\
    |                               render
    \* -------------------------------------------------------------------------------- */
    const meetingIso = (meetingdateVal || dateVal) ? `${meetingdateVal || dateVal}T00:00:00.000Z` : '';

    return (
        <LayoutSite>
            <div className="newrace">
                <h2 className="newrace__title">New Race</h2>

                {/* Race info */}
                <div className="newrace__section">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div className="newrace__grid">
                        <label>Date</label>
                        <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)} className="newrace__dateinput" />

                        <label>Time</label>
                        <input type="time" value={timeVal} onChange={e => setTimeVal(e.target.value)} className="newrace__dateinput" />

                        <label>Meeting date</label>
                        <div className="newrace__sic-row">
                            <input type="date" value={meetingdateVal} onChange={e => setMeetingdateVal(e.target.value)} className="newrace__dateinput" />
                            {meetingIso && <span className="newrace__sic-name" style={{ fontFamily: 'monospace' }}>{meetingIso}</span>}
                        </div>

                        <label>Race #</label>
                        <InputNumber size="small" value={number} onChange={v => setNumber(v ?? 1)} min={1} className="newrace__input" />

                        <label>SIC</label>
                        <div className="newrace__sic-row">
                            <AutoComplete
                                size="small"
                                value={sic}
                                onChange={v => { setSic(v); if (sicMap[v]) setTrackname(sicMap[v]); else setTrackname(''); }}
                                placeholder="GA"
                                options={sicOptions}
                                filterOption={(input, option) =>
                                    !(option as any)?.options && ((option as any)?.value as string)?.toLowerCase().includes(input.toLowerCase())
                                }
                                className="newrace__input newrace__sic"
                            />
                            {trackname && <span className="newrace__sic-name">{trackname}</span>}
                        </div>


                        <label>Race name</label>
                        <Input size="small" value={name} onChange={e => setName(e.target.value)} className="newrace__input" />

                        <label>Distance (m)</label>
                        <InputNumber size="small" value={distance} onChange={v => {
                            const newDist = v ?? 0;
                            setCompetitors(prev => prev.map(c => c.distance === distance ? { ...c, distance: newDist } : c));
                            setDistance(newDist);
                        }} min={0} className="newrace__input" />

                        <label>Type</label>
                        <Select size="small" value={type} onChange={setType} className="newrace__select"
                            options={RACE_TYPES.map(t => ({ value: t, label: t }))} />

                        <label>Track code</label>
                        <AutoComplete
                            size="small"
                            value={trackcode}
                            onChange={setTrackcode}
                            options={(codesMap[sic.toUpperCase()] || []).map(c => ({ value: c, label: c }))}
                            className="newrace__input newrace__sic"
                        />

                        <label>Source</label>
                        <Input size="small" value={source} onChange={e => setSource(e.target.value)} className="newrace__input" />

                        <label>Comment</label>
                        <Input size="small" value={comment} onChange={e => setComment(e.target.value)} className="newrace__input" />
                    </div>
                    <div>
                        <Button size="small" onClick={() => jsonInputRef.current?.click()}>Import JSON</Button>
                        <input ref={jsonInputRef} type="file" accept=".json" style={{ display: 'none' }}
                            onChange={e => { const f = e.target.files?.[0]; if (f) { handleImportJson(f); e.target.value = ''; } }} />
                    </div>
                    </div>
                </div>

                {/* Competitors */}
                <div className="newrace__section">
                    <div className="newrace__section-title">Competitors</div>
                    <div className="newrace__competitors">
                        <div className="newrace__comp-header">
                            <span className="newrace__comp-col--ord" />
                            <span className="newrace__comp-col--no">#</span>
                            <span className="newrace__comp-col--name">Name</span>
                            <span className="newrace__comp-col--status">Status</span>
                            <span className="newrace__comp-col--draw">Draw</span>
                            <span className="newrace__comp-col--dist">Dist.</span>
                            <span className="newrace__comp-col--driver">Driver / Jockey</span>
                            <span className="newrace__comp-col--trainer">Trainer</span>
                            <span className="newrace__comp-col--owner">Owner</span>
                            <span className="newrace__comp-col--del" />
                        </div>
                        {competitors.map((c, idx) => (
                            <div key={idx}
                                className={`newrace__comp-row${dragOver === idx ? ' newrace__comp-row--dragover' : ''}`}
                                draggable
                                onDragStart={() => { dragIdx.current = idx; }}
                                onDragOver={e => { e.preventDefault(); setDragOver(idx); }}
                                onDragLeave={() => setDragOver(null)}
                                onDrop={() => handleDrop(idx)}
                            >
                                <div className="newrace__comp-col--ord">
                                    <HolderOutlined className="newrace__drag-handle" />
                                </div>
                                <span className="newrace__comp-col--no newrace__comp-no">{c.number}</span>
                                <Input size="small" value={c.name}
                                    onChange={e => updateCompetitor(idx, { name: e.target.value })}
                                    className="newrace__comp-col--name" />
                                <Select size="small" value={c.status}
                                    onChange={v => updateCompetitor(idx, { status: v })}
                                    options={STATUS_OPTIONS}
                                    className="newrace__comp-col--status newrace__select" />
                                <InputNumber size="small" value={c.draw} min={0}
                                    onChange={v => updateCompetitor(idx, { draw: v ?? 0 })}
                                    className="newrace__comp-col--draw" />
                                <InputNumber size="small" value={c.distance} min={0}
                                    onChange={v => updateCompetitor(idx, { distance: v ?? 0 })}
                                    className="newrace__comp-col--dist" />
                                <Input size="small" value={c.driver}
                                    onChange={e => updateCompetitor(idx, { driver: e.target.value })}
                                    className="newrace__comp-col--driver" />
                                <Input size="small" value={c.trainer}
                                    onChange={e => updateCompetitor(idx, { trainer: e.target.value })}
                                    className="newrace__comp-col--trainer" />
                                <Input size="small" value={c.owner}
                                    onChange={e => updateCompetitor(idx, { owner: e.target.value })}
                                    className="newrace__comp-col--owner" />
                                <Button size="small" danger type="text" icon={<DeleteOutlined />}
                                    onClick={() => removeCompetitor(idx)}
                                    className="newrace__comp-col--del" />
                            </div>
                        ))}
                    </div>
                    <Button icon={<PlusOutlined />} size="small" onClick={addCompetitor} style={{ marginTop: 8 }}>
                        Add competitor
                    </Button>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 16, marginLeft: 17 }}>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} style={{ width: 120 }}>
                        Create race
                    </Button>
                    <Button onClick={handleCancel} style={{ width: 120 }}>Reset</Button>
                    <Tooltip title={!dateVal || !sic.trim() || !number ? 'Date, SIC and race number are required' : undefined}>
                        <Button onClick={exportjson} style={{ width: 120, marginLeft: 24 }} disabled={!dateVal || !sic.trim() || !number}>Export JSON</Button>
                        <Button style={{ width: 120, marginLeft: 10 }} onClick={exportracing} disabled={!dateVal || !sic.trim() || !number}>Export for Racing</Button>
                    </Tooltip>
                </div>
            </div>
        </LayoutSite>
    );
};
